import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import type { KPIFilterState } from "@/components/dashboard/KPIFilters";
import { CLOSED_MATCH_STATUSES } from "@/utils/matches/status";

const PIPELINE_STAGES = ["matched", "screening", "sent", "interview", "offer", "hired", "closed"] as const;
const ACTIVE_STAGES = ["matched", "screening", "sent", "interview", "offer"] as const;
const MIN_DATE = new Date("2026-02-01T00:00:00Z"); // No match history before this date

export interface PipelineFunnelData {
	status: string;
	count: number;
}

export interface ConversionData {
	from_stage: string;
	to_stage: string;
	count: number;
	percentage?: number;
}

export interface ScoreDistributionBucket {
	range: string;
	count: number;
}

export interface RecruiterData {
	id: string;
	name: string;
	openJobs: number;
	matchesCreated: number;
	hires: number;
}

export interface RecruiterActivityData {
	id: string;
	name: string;
	totalActivities: number;
	progressions: number;
	regressions: number;
	screenings: number;
	interviews: number;
	hires: number;
	refusals: number;
	conversionRate: number; // % of progressions that led to hire
	avgResponseTime: number | null; // Avg days between their transitions
	mostActiveStage: string | null;
	stageBreakdown: { stage: string; count: number }[];
}

export interface HistoricalVelocityData {
	period: string;
	matches: number;
	interviews: number;
	hires: number;
}

export interface MatchesPerJobData {
	avg: number;
	min: number;
	max: number;
}

export interface TimeInStageData {
	stage: string;
	avgDays: number;
}

export interface PipelineHealthData {
	openJobs: number;
	activeCandidates: number;
	matchesInProgress: number;
	pendingMatches: number;
}

export interface CandidateCreationTrendData {
	period: string;
	count: number;
	[status: string]: number | string; // Dynamic status counts per period
}

export interface CandidatesByStatusData {
	status: string;
	count: number;
}

interface WorkforceFinancials {
	dailyRevenue: number;
	dailyCost: number;
	dailyMargin: number;
	marginPct: number;
	dailyBenchCost: number;
	monthlyRevenue: number;
	monthlyCost: number;
	monthlyMargin: number;
	monthlyBenchCost: number;
}

interface ContractTypeBreakdown {
	type: string;
	count: number;
}

interface BenchReasonBreakdown {
	reason: string;
	count: number;
	dailyCost: number;
}

interface ExpiringAssignment {
	id: string;
	candidateName: string;
	jobTitle: string;
	expectedEndDate: string;
	daysRemaining: number;
	dailyRevenue: number | null;
}

export interface WorkforceKPIData {
	totalAssignments: number;
	activeAssignments: number;
	benchCount: number;
	onHoldCount: number;
	completedCount: number;
	terminatedCount: number;
	avgAssignmentDuration: number | null;
	utilizationRate: number;
	byStatus: { status: string; count: number }[];
	assignmentTrend: { period: string; created: number; ended: number }[];
	financials: WorkforceFinancials;
	contractTypes: ContractTypeBreakdown[];
	benchReasons: BenchReasonBreakdown[];
	expiringSoon: ExpiringAssignment[];
}

export interface DashboardKPIs {
	pipelineFunnel: PipelineFunnelData[];
	matchesPerJob: MatchesPerJobData;
	conversionRates: ConversionData[];
	dropOffRates: ConversionData[];
	timeToHire: number | null;
	timeToFill: number | null;
	timeInStage: TimeInStageData[];
	publicAppRatio: { publicApps: number; matches: number; ratio: number };
	aiScoreVsOutcome: {
		hired: { avg: number; count: number };
		interview: { avg: number; count: number };
		closed: { avg: number; count: number };
	};
	scoreDistribution: ScoreDistributionBucket[];
	recruiters: RecruiterData[];
	recruiterActivity: RecruiterActivityData[];
	offerAcceptanceRate: { offers: number; accepted: number };
	pipelineHealth: PipelineHealthData;
	historicalVelocity: HistoricalVelocityData[];
	candidatesCreated: number;
	candidateCreationTrend: CandidateCreationTrendData[];
	candidatesByStatus: CandidatesByStatusData[];
	workforceKPIs: WorkforceKPIData;
	resolvedDateFrom: string | null;
	resolvedDateTo: string | null;
}

interface ResolvedFilters {
	userId: string | null;
	dateFrom: string | null;
	dateTo: string | null;
}

// Helper: enforce minimum date filter
function enforceMinDate(date: Date | null): Date | null {
	if (!date) return MIN_DATE;
	return date < MIN_DATE ? MIN_DATE : date;
}

// Helper: extract recruiter UUID from assigned_recruiter field
// Handles: plain UUID string, JSON string like '{"id":"uuid",...}', or JSON object {id:"uuid"}
function extractRecruiterId(value: any): string | null {
	if (!value) return null;
	if (typeof value === "string") {
		// Plain UUID
		if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value))
			return value;
		// Try JSON parse
		try {
			const parsed = JSON.parse(value);
			if (parsed && typeof parsed === "object" && parsed.id)
				return parsed.id;
		} catch {
			// Not JSON, not UUID — ignore
		}
		return null;
	}
	if (typeof value === "object" && value.id)
		return value.id;
	return null;
}

// Helper: get job IDs for a specific recruiter (handles JSON-formatted assigned_recruiter)
async function getJobIdsForRecruiter(userId: string): Promise<string[]> {
	const { data, error } = await supabase
		.from("jobs")
		.select("id, assigned_recruiter")
		.not("assigned_recruiter", "is", null);

	if (error) throw error;
	return (data || [])
		.filter((j: any) => extractRecruiterId(j.assigned_recruiter) === userId)
		.map((j: any) => j.id);
}

// Helper: get ISO week number (Monday-based)
function getISOWeek(date: Date): string {
	const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
	const dayNum = d.getUTCDay() || 7;
	d.setUTCDate(d.getUTCDate() + 4 - dayNum);
	const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
	const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
	return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}

async function fetchPipelineFunnel(filters: ResolvedFilters): Promise<PipelineFunnelData[]> {
	// Pipeline funnel is a snapshot of current match statuses.
	// When date filters are applied, we include matches that either:
	// 1. Were created in the period, OR
	// 2. Had a status change (activity) in the period (from history_matches)
	let matchIds: Set<string> | null = null;

	if (filters.dateFrom || filters.dateTo) {
		matchIds = new Set<string>();

		// Matches created in the period
		let createdQuery = supabase.from("matches").select("id, created_at");
		if (filters.dateFrom)
			createdQuery = createdQuery.gte("created_at", filters.dateFrom);
		if (filters.dateTo)
			createdQuery = createdQuery.lte("created_at", filters.dateTo);
		const { data: createdMatches } = await createdQuery;
		(createdMatches || []).forEach((m: any) => matchIds!.add(m.id));

		// Matches with activity in the period (status changes)
		let histQuery = supabase
			.from("history_matches" as any)
			.select("match_id, changed_at");
		if (filters.dateFrom)
			histQuery = histQuery.gte("changed_at", filters.dateFrom);
		if (filters.dateTo)
			histQuery = histQuery.lte("changed_at", filters.dateTo);
		if (filters.userId)
			histQuery = histQuery.eq("changed_by", filters.userId);
		const { data: histMatches } = await histQuery;
		(histMatches || []).forEach((h: any) => {
			if (h.match_id) matchIds!.add(h.match_id);
		});
	}

	// Fetch current status of relevant matches
	let query = supabase.from("matches").select("id, status, job_id");
	if (matchIds)
		query = query.in("id", Array.from(matchIds));

	const { data, error } = await query;
	if (error) throw error;

	let rows = data || [];

	// User filter: filter by job's assigned_recruiter
	if (filters.userId) {
		const jobIds = await getJobIdsForRecruiter(filters.userId);
		rows = rows.filter((m: any) => jobIds.includes(m.job_id));
	}

	const counts = new Map<string, number>();
	PIPELINE_STAGES.forEach(s => counts.set(s, 0));

	rows.forEach((m: any) => {
		if (m.status) {
			if (CLOSED_MATCH_STATUSES.includes(m.status))
				counts.set("closed", (counts.get("closed") || 0) + 1);
			else
				counts.set(m.status, (counts.get(m.status) || 0) + 1);
		}
	});

	return PIPELINE_STAGES.map(status => ({
		status,
		count: counts.get(status) || 0,
	}));
}

async function fetchMatchesPerJob(filters: ResolvedFilters): Promise<MatchesPerJobData> {
	const { data: allJobs, error: jobsError } = await supabase
		.from("jobs")
		.select("id, assigned_recruiter")
		.eq("status", "open");

	if (jobsError) throw jobsError;

	let jobs = allJobs || [];
	if (filters.userId)
		jobs = jobs.filter((j: any) => extractRecruiterId(j.assigned_recruiter) === filters.userId);

	if (jobs.length === 0) return { avg: 0, min: 0, max: 0 };

	const jobIds = jobs.map((j: any) => j.id);

	let matchQuery = supabase.from("matches").select("job_id").in("job_id", jobIds);

	if (filters.dateFrom)
		matchQuery = matchQuery.gte("created_at", filters.dateFrom);
	if (filters.dateTo)
		matchQuery = matchQuery.lte("created_at", filters.dateTo);

	const { data: matches, error: matchError } = await matchQuery;
	if (matchError) throw matchError;

	const countMap = new Map<string, number>();
	jobIds.forEach(id => countMap.set(id, 0));
	(matches || []).forEach((m: any) => {
		if (m.job_id)
			countMap.set(m.job_id, (countMap.get(m.job_id) || 0) + 1);
	});

	const counts = Array.from(countMap.values());
	const sum = counts.reduce((a, b) => a + b, 0);

	return {
		avg: Math.round((sum / counts.length) * 10) / 10,
		min: Math.min(...counts),
		max: Math.max(...counts),
	};
}

async function fetchConversionRates(filters: ResolvedFilters): Promise<{ conversions: ConversionData[]; dropOffs: ConversionData[] }> {
	let query = supabase
		.from("history_matches" as any)
		.select("old_value, new_value, changed_fields, changed_by, changed_at")
		//.contains("changed_fields", ["status"]);

	if (filters.dateFrom)
		query = query.gte("changed_at", filters.dateFrom);
	if (filters.dateTo)
		query = query.lte("changed_at", filters.dateTo);
	if (filters.userId)
		query = query.eq("changed_by", filters.userId);

	const { data, error } = await query;
	if (error) throw error;

	const transitionMap = new Map<string, number>();
	const fromTotals = new Map<string, number>();

	(data || []).forEach((row: any) => {
		const from = row.old_value?.status;
		const to = row.new_value?.status;
		if (!from || !to) return;

		const key = `${from}→${to}`;
		transitionMap.set(key, (transitionMap.get(key) || 0) + 1);
		fromTotals.set(from, (fromTotals.get(from) || 0) + 1);
	});

	const conversions: ConversionData[] = [];
	const dropOffs: ConversionData[] = [];

	transitionMap.forEach((count, key) => {
		const [from_stage, to_stage] = key.split("→");
		const total = fromTotals.get(from_stage) || 1;
		const entry: ConversionData = {
			from_stage,
			to_stage,
			count,
			percentage: Math.round((count / total) * 100),
		};

		if (CLOSED_MATCH_STATUSES.includes(to_stage as any))
			dropOffs.push(entry);
		else
			conversions.push(entry);
	});

	return { conversions, dropOffs };
}

async function fetchTimeToHire(filters: ResolvedFilters): Promise<number | null> {
	let query = supabase
		.from("history_matches" as any)
		.select("match_id, changed_at, old_value, new_value, changed_by")
		//.contains("changed_fields", ["status"]);

	if (filters.dateFrom)
		query = query.gte("changed_at", filters.dateFrom);
	if (filters.dateTo)
		query = query.lte("changed_at", filters.dateTo);
	if (filters.userId)
		query = query.eq("changed_by", filters.userId);

	const { data: hiredHistory, error } = await query;
	if (error) throw error;

	const hireEvents = (hiredHistory || []).filter(
		(row: any) => row.new_value?.status === "hired" && row.old_value?.status !== "hired"
	);

	if (hireEvents.length === 0) return null;

	const matchIds = [...new Set(hireEvents.map((h: any) => h.match_id))];
	const { data: matches, error: matchError } = await supabase
		.from("matches")
		.select("id, created_at")
		.in("id", matchIds);

	if (matchError) throw matchError;

	const matchCreatedMap = new Map<string, string>();
	(matches || []).forEach((m: any) => matchCreatedMap.set(m.id, m.created_at));

	let totalDays = 0;
	let count = 0;

	hireEvents.forEach((h: any) => {
		const created = matchCreatedMap.get(h.match_id);
		if (created) {
			const days = (new Date(h.changed_at).getTime() - new Date(created).getTime()) / 86400000;
			totalDays += days;
			count++;
		}
	});

	return count > 0 ? Math.round((totalDays / count) * 10) / 10 : null;
}

async function fetchTimeToFill(filters: ResolvedFilters): Promise<number | null> {
	let query = supabase
		.from("history_matches" as any)
		.select("match_id, changed_at, old_value, new_value, changed_by")
		//.contains("changed_fields", ["status"]);

	if (filters.dateFrom)
		query = query.gte("changed_at", filters.dateFrom);
	if (filters.dateTo)
		query = query.lte("changed_at", filters.dateTo);

	const { data: hiredHistory, error } = await query;
	if (error) throw error;

	const hireEvents = (hiredHistory || []).filter(
		(row: any) => row.new_value?.status === "hired" && row.old_value?.status !== "hired"
	);

	if (hireEvents.length === 0) return null;

	const matchIds = [...new Set(hireEvents.map((h: any) => h.match_id))];
	const { data: matches, error: matchError } = await supabase
		.from("matches")
		.select("id, job_id")
		.in("id", matchIds);

	if (matchError) throw matchError;

	const jobIds = [...new Set((matches || []).map((m: any) => m.job_id).filter(Boolean))];

	// Apply user filter on jobs
	const { data: allJobs, error: jobError } = await supabase
		.from("jobs")
		.select("id, created_at, assigned_recruiter")
		.in("id", jobIds);

	let jobs = allJobs || [];
	if (filters.userId)
		jobs = jobs.filter((j: any) => extractRecruiterId(j.assigned_recruiter) === filters.userId);
	if (jobError) throw jobError;

	const jobCreatedMap = new Map<string, string>();
	(jobs || []).forEach((j: any) => jobCreatedMap.set(j.id, j.created_at));

	const matchJobMap = new Map<string, string>();
	(matches || []).forEach((m: any) => {
		if (m.job_id) matchJobMap.set(m.id, m.job_id);
	});

	const jobFirstHire = new Map<string, number>();
	hireEvents.forEach((h: any) => {
		const jobId = matchJobMap.get(h.match_id);
		if (!jobId || !jobCreatedMap.has(jobId)) return;
		const hiredAt = new Date(h.changed_at).getTime();
		const current = jobFirstHire.get(jobId);
		if (!current || hiredAt < current)
			jobFirstHire.set(jobId, hiredAt);
	});

	let totalDays = 0;
	let count = 0;
	jobFirstHire.forEach((hiredAt, jobId) => {
		const created = jobCreatedMap.get(jobId);
		if (created) {
			const days = (hiredAt - new Date(created).getTime()) / 86400000;
			totalDays += days;
			count++;
		}
	});

	return count > 0 ? Math.round((totalDays / count) * 10) / 10 : null;
}

async function fetchTimeInStage(filters: ResolvedFilters): Promise<TimeInStageData[]> {
	let query = supabase
		.from("history_matches" as any)
		.select("match_id, changed_at, old_value, new_value, changed_fields, changed_by")
		//.contains("changed_fields", ["status"])
		.order("changed_at", { ascending: true });

	if (filters.dateFrom)
		query = query.gte("changed_at", filters.dateFrom);
	if (filters.dateTo)
		query = query.lte("changed_at", filters.dateTo);
	if (filters.userId)
		query = query.eq("changed_by", filters.userId);

	const { data, error } = await query;
	if (error) throw error;

	const matchEvents = new Map<string, { changed_at: string; from: string; to: string }[]>();
	(data || []).forEach((row: any) => {
		const from = row.old_value?.status;
		const to = row.new_value?.status;
		if (!from || !to) return;

		const events = matchEvents.get(row.match_id) || [];
		events.push({ changed_at: row.changed_at, from, to });
		matchEvents.set(row.match_id, events);
	});

	const stageDurations = new Map<string, number[]>();

	matchEvents.forEach((events) => {
		events.sort((a, b) => new Date(a.changed_at).getTime() - new Date(b.changed_at).getTime());
		for (let i = 0; i < events.length - 1; i++) {
			const days = (new Date(events[i + 1].changed_at).getTime() - new Date(events[i].changed_at).getTime()) / 86400000;
			const stage = events[i].to;
			const durations = stageDurations.get(stage) || [];
			durations.push(days);
			stageDurations.set(stage, durations);
		}
	});

	return ACTIVE_STAGES
		.filter(stage => stageDurations.has(stage))
		.map(stage => {
			const durations = stageDurations.get(stage)!;
			const avg = durations.reduce((a, b) => a + b, 0) / durations.length;
			return { stage, avgDays: Math.round(avg * 10) / 10 };
		});
}

// KPI #4: Public Application Ratio — ratio of public (external) applications vs total recruiter-managed matches
async function fetchPublicAppRatio(filters: ResolvedFilters): Promise<{ publicApps: number; matches: number; ratio: number }> {
	let appsQuery = supabase
		.from("jobs_applications" as any)
		.select("job_id, date_created");

	if (filters.dateFrom)
		appsQuery = appsQuery.gte("date_created", filters.dateFrom);
	if (filters.dateTo)
		appsQuery = appsQuery.lte("date_created", filters.dateTo);

	const { data: appsData, error: appsError } = await appsQuery;
	if (appsError) throw appsError;

	let appRows = appsData || [];
	if (filters.userId) {
		const jobIds = await getJobIdsForRecruiter(filters.userId);
		appRows = appRows.filter((r: any) => jobIds.includes(r.job_id));
	}
	const publicApps = appRows.length;

	let matchQuery = supabase.from("matches").select("job_id, created_at");
	if (filters.dateFrom)
		matchQuery = matchQuery.gte("created_at", filters.dateFrom);
	if (filters.dateTo)
		matchQuery = matchQuery.lte("created_at", filters.dateTo);

	const { data: matchData, error: matchError } = await matchQuery;
	if (matchError) throw matchError;

	let matchRows = matchData || [];
	if (filters.userId) {
		const jobIds = await getJobIdsForRecruiter(filters.userId);
		matchRows = matchRows.filter((m: any) => jobIds.includes(m.job_id));
	}
	const matches = matchRows.length;

	const total = publicApps + matches;
	const ratio = total > 0 ? Math.round((publicApps / total) * 100) : 0;

	return { publicApps, matches, ratio };
}

async function fetchAiScoreVsOutcome(filters: ResolvedFilters) {
	// Find matches that transitioned to hired/interview/closed within the date range
	// by looking at history_matches (last modified date), not match created_at
	let histQuery = supabase
		.from("history_matches" as any)
		.select("match_id, new_value, changed_at, changed_by");

	if (filters.dateFrom)
		histQuery = histQuery.gte("changed_at", filters.dateFrom);
	if (filters.dateTo)
		histQuery = histQuery.lte("changed_at", filters.dateTo);
	if (filters.userId)
		histQuery = histQuery.eq("changed_by", filters.userId);

	const { data: histData, error: histError } = await histQuery;
	if (histError) throw histError;

	// Collect match IDs by their latest relevant status change in the period
	const matchOutcome = new Map<string, string>();
	(histData || []).forEach((row: any) => {
		const toStatus = row.new_value?.status;
		if (toStatus === "hired" || toStatus === "interview")
			matchOutcome.set(row.match_id, toStatus);
		else if (CLOSED_MATCH_STATUSES.includes(toStatus))
			matchOutcome.set(row.match_id, "closed");
	});

	const matchIds = Array.from(matchOutcome.keys());
	if (matchIds.length === 0)
		return {
			hired: { avg: 0, count: 0 },
			interview: { avg: 0, count: 0 },
			closed: { avg: 0, count: 0 },
		};

	// Fetch scores for those matches
	const { data: matches, error: matchError } = await supabase
		.from("matches")
		.select("id, score, job_id")
		.in("id", matchIds);
	if (matchError) throw matchError;

	let rows = (matches || []).map((m: any) => ({
		...m,
		outcome: matchOutcome.get(m.id),
	}));

	if (filters.userId) {
		const jobIds = await getJobIdsForRecruiter(filters.userId);
		rows = rows.filter((m: any) => jobIds.includes(m.job_id));
	}

	const hired = rows.filter((m: any) => m.outcome === "hired");
	const interview = rows.filter((m: any) => m.outcome === "interview");
	const closed = rows.filter((m: any) => m.outcome === "closed");

	const avgScore = (items: any[]) => {
		const withScore = items.filter(i => i.score != null);
		if (withScore.length === 0) return 0;
		return Math.round(withScore.reduce((sum, i) => sum + i.score, 0) / withScore.length);
	};

	return {
		hired: { avg: avgScore(hired), count: hired.length },
		interview: { avg: avgScore(interview), count: interview.length },
		closed: { avg: avgScore(closed), count: closed.length },
	};
}

async function fetchScoreDistribution(filters: ResolvedFilters): Promise<ScoreDistributionBucket[]> {
	let query = supabase.from("matches").select("score, job_id, created_at");

	if (filters.dateFrom)
		query = query.gte("created_at", filters.dateFrom);
	if (filters.dateTo)
		query = query.lte("created_at", filters.dateTo);

	const { data, error } = await query;
	if (error) throw error;

	let rows = data || [];

	if (filters.userId) {
		const jobIds = await getJobIdsForRecruiter(filters.userId);
		rows = rows.filter((m: any) => jobIds.includes(m.job_id));
	}

	const buckets = [
		{ range: "0-20", count: 0 },
		{ range: "21-40", count: 0 },
		{ range: "41-60", count: 0 },
		{ range: "61-80", count: 0 },
		{ range: "81-100", count: 0 },
	];

	rows.forEach((m: any) => {
		if (m.score == null) return;
		if (m.score <= 20) buckets[0].count++;
		else if (m.score <= 40) buckets[1].count++;
		else if (m.score <= 60) buckets[2].count++;
		else if (m.score <= 80) buckets[3].count++;
		else buckets[4].count++;
	});

	return buckets;
}

async function fetchRecruiters(filters: ResolvedFilters): Promise<RecruiterData[]> {
	const { data: openJobs, error: jobsError } = await supabase
		.from("jobs")
		.select("id, assigned_recruiter")
		.eq("status", "open")
		.not("assigned_recruiter", "is", null);

	if (jobsError) throw jobsError;

	// Extract recruiter IDs, handling both UUID and JSON formats
	const jobsWithRecruiter = (openJobs || [])
		.map((j: any) => ({ ...j, recruiterId: extractRecruiterId(j.assigned_recruiter) }))
		.filter((j: any) => j.recruiterId != null);

	// Apply user filter
	const filteredJobs = filters.userId
		? jobsWithRecruiter.filter((j: any) => j.recruiterId === filters.userId)
		: jobsWithRecruiter;

	const recruiterIds = [...new Set(filteredJobs.map((j: any) => j.recruiterId as string))];

	if (recruiterIds.length === 0) return [];

	const { data: profiles, error: profilesError } = await supabase
		.from("profiles")
		.select("id, first_name, last_name")
		.in("id", recruiterIds);

	if (profilesError) throw profilesError;

	const profileMap = new Map<string, string>();
	(profiles || []).forEach((p: any) => {
		profileMap.set(p.id, [p.first_name, p.last_name].filter(Boolean).join(" ") || "Unknown");
	});

	const recruiterJobIds = new Map<string, string[]>();
	filteredJobs.forEach((j: any) => {
		const jobs = recruiterJobIds.get(j.recruiterId) || [];
		jobs.push(j.id);
		recruiterJobIds.set(j.recruiterId, jobs);
	});

	const allJobIds = filteredJobs.map((j: any) => j.id);

	// Hires: matches with status="hired" on recruiter's jobs
	let matchQuery = supabase
		.from("matches")
		.select("job_id")
		.eq("status", "hired")
		.in("job_id", allJobIds);

	if (filters.dateFrom)
		matchQuery = matchQuery.gte("created_at", filters.dateFrom);
	if (filters.dateTo)
		matchQuery = matchQuery.lte("created_at", filters.dateTo);

	const { data: hiredMatches, error: matchError } = await matchQuery;
	if (matchError) throw matchError;

	const hiresByJob = new Map<string, number>();
	(hiredMatches || []).forEach((m: any) => {
		hiresByJob.set(m.job_id, (hiresByJob.get(m.job_id) || 0) + 1);
	});

	// Matches created: history_matches INSERT events by each recruiter
	let createdQuery = supabase
		.from("history_matches" as any)
		.select("match_id, changed_by, changed_at")
		.eq("operation", "INSERT")
		.in("changed_by", recruiterIds);

	if (filters.dateFrom)
		createdQuery = createdQuery.gte("changed_at", filters.dateFrom);
	if (filters.dateTo)
		createdQuery = createdQuery.lte("changed_at", filters.dateTo);

	const { data: createdHistory, error: createdError } = await createdQuery;
	if (createdError) throw createdError;

	const matchesCreatedByRecruiter = new Map<string, number>();
	(createdHistory || []).forEach((h: any) => {
		if (h.changed_by)
			matchesCreatedByRecruiter.set(h.changed_by, (matchesCreatedByRecruiter.get(h.changed_by) || 0) + 1);
	});

	return recruiterIds.map(id => {
		const jobs = recruiterJobIds.get(id) || [];
		const hires = jobs.reduce((sum, jobId) => sum + (hiresByJob.get(jobId) || 0), 0);
		return {
			id,
			name: profileMap.get(id) || "Unknown",
			openJobs: jobs.length,
			matchesCreated: matchesCreatedByRecruiter.get(id) || 0,
			hires,
		};
	});
}

async function fetchRecruiterActivity(filters: ResolvedFilters): Promise<RecruiterActivityData[]> {
	// Fetch all status change history
	let query = supabase
		.from("history_matches" as any)
		.select("match_id, changed_by, changed_at, old_value, new_value, changed_fields")
		//.contains("changed_fields", ["status"])
		.not("changed_by", "is", null);

	if (filters.dateFrom)
		query = query.gte("changed_at", filters.dateFrom);
	if (filters.dateTo)
		query = query.lte("changed_at", filters.dateTo);
	if (filters.userId)
		query = query.eq("changed_by", filters.userId);

	const { data: history, error } = await query;
	if (error) throw error;

	// Define stage progression order (closed statuses are all at level 0 = not a progression)
	const stageOrder: Record<string, number> = {
		"applied": 0,
		"matched": 1,
		"screening": 2,
		"sent": 3,
		"interview": 4,
		"offer": 5,
		"hired": 6,
	};

	// Group by recruiter
	const recruiterMap = new Map<string, {
		activities: any[];
		stageChanges: Map<string, number>;
	}>();

	(history || []).forEach((h: any) => {
		const recruiterId = h.changed_by;
		const fromStatus = h.old_value?.status;
		const toStatus = h.new_value?.status;

		if (!recruiterId || !fromStatus || !toStatus) return;

		if (!recruiterMap.has(recruiterId)) {
			recruiterMap.set(recruiterId, {
				activities: [],
				stageChanges: new Map(),
			});
		}

		const recruiterData = recruiterMap.get(recruiterId)!;
		recruiterData.activities.push(h);

		// Count stage changes
		recruiterData.stageChanges.set(toStatus, (recruiterData.stageChanges.get(toStatus) || 0) + 1);
	});

	const recruiterIds = Array.from(recruiterMap.keys());

	if (recruiterIds.length === 0) return [];

	// Fetch recruiter names
	const { data: profiles, error: profilesError } = await supabase
		.from("profiles")
		.select("id, first_name, last_name")
		.in("id", recruiterIds);

	if (profilesError) throw profilesError;

	const profileMap = new Map<string, string>();
	(profiles || []).forEach((p: any) => {
		profileMap.set(p.id, [p.first_name, p.last_name].filter(Boolean).join(" ") || "Unknown");
	});

	// Calculate metrics per recruiter
	return recruiterIds.map(id => {
		const data = recruiterMap.get(id)!;
		const activities = data.activities;

		let progressions = 0;
		let regressions = 0;
		let screenings = 0;
		let interviews = 0;
		let hires = 0;
		let refusals = 0;
		const responseTimes: number[] = [];

		// Sort activities by match and time to calculate response times
		const byMatch = new Map<string, any[]>();
		activities.forEach((a: any) => {
			const list = byMatch.get(a.match_id) || [];
			list.push(a);
			byMatch.set(a.match_id, list);
		});

		byMatch.forEach(matchActivities => {
			matchActivities.sort((a, b) => new Date(a.changed_at).getTime() - new Date(b.changed_at).getTime());

			for (let i = 0; i < matchActivities.length; i++) {
				const activity = matchActivities[i];
				const fromStatus = activity.old_value?.status;
				const toStatus = activity.new_value?.status;

				// Count progressions/regressions
				const fromOrder = stageOrder[fromStatus] || 0;
				const toOrder = stageOrder[toStatus] || 0;

				if (toStatus === "screening") screenings++;
				if (toStatus === "interview") interviews++;
				if (toStatus === "hired") hires++;
				if (CLOSED_MATCH_STATUSES.includes(toStatus)) refusals++;

				const isClosedTransition = CLOSED_MATCH_STATUSES.includes(toStatus);
				const isFromClosed = CLOSED_MATCH_STATUSES.includes(fromStatus);

				if (isFromClosed && !isClosedTransition) {
					// Moving FROM a closed status back to active = progression (recruiter "saved" the match)
					progressions++;
				} else if (!isClosedTransition && toOrder > fromOrder) {
					// Normal forward movement in pipeline
					progressions++;
				} else if (isClosedTransition) {
					// Moving to a closed status = regression
					regressions++;
				} else if (toOrder > 0 && toOrder < fromOrder) {
					// Moving backwards in active pipeline
					regressions++;
				}

				// Calculate response time (time since previous activity by this recruiter on this match)
				if (i > 0) {
					const prevActivity = matchActivities[i - 1];
					const days = (new Date(activity.changed_at).getTime() - new Date(prevActivity.changed_at).getTime()) / 86400000;
					responseTimes.push(days);
				}
			}
		});

		const avgResponseTime = responseTimes.length > 0
			? Math.round((responseTimes.reduce((sum, t) => sum + t, 0) / responseTimes.length) * 10) / 10
			: null;

		// Find most active stage
		const stageBreakdown = Array.from(data.stageChanges.entries())
			.map(([stage, count]) => ({ stage, count }))
			.sort((a, b) => b.count - a.count);

		const mostActiveStage = stageBreakdown.length > 0 ? stageBreakdown[0].stage : null;

		// Conversion rate: hires / progressions (if any progressions)
		const conversionRate = progressions > 0
			? Math.round((hires / progressions) * 100)
			: 0;

		return {
			id,
			name: profileMap.get(id) || "Unknown",
			totalActivities: activities.length,
			progressions,
			regressions,
			screenings,
			interviews,
			hires,
			refusals,
			conversionRate,
			avgResponseTime,
			mostActiveStage,
			stageBreakdown,
		};
	}).sort((a, b) => b.totalActivities - a.totalActivities); // Sort by most active
}

async function fetchOfferAcceptanceRate(filters: ResolvedFilters) {
	let query = supabase
		.from("history_matches" as any)
		.select("match_id, old_value, new_value, changed_fields, changed_by, changed_at")
		//.contains("changed_fields", ["status"]);

	if (filters.dateFrom)
		query = query.gte("changed_at", filters.dateFrom);
	if (filters.dateTo)
		query = query.lte("changed_at", filters.dateTo);
	if (filters.userId)
		query = query.eq("changed_by", filters.userId);

	const { data, error } = await query;
	if (error) throw error;

	const offerMatchIds = new Set<string>();
	const hiredFromOffer = new Set<string>();

	(data || []).forEach((row: any) => {
		if (row.new_value?.status === "offer")
			offerMatchIds.add(row.match_id);
		if (row.old_value?.status === "offer" && row.new_value?.status === "hired")
			hiredFromOffer.add(row.match_id);
	});

	return { offers: offerMatchIds.size, accepted: hiredFromOffer.size };
}

async function fetchPipelineHealth(filters: ResolvedFilters): Promise<PipelineHealthData> {
	// Pipeline health is a snapshot — user filter narrows by recruiter
	// For openJobs with user filter, we must filter client-side to handle JSON assigned_recruiter
	let openJobsCount: number;
	if (filters.userId) {
		const { data: allOpenJobs, error: jobsErr } = await supabase
			.from("jobs")
			.select("id, assigned_recruiter")
			.eq("status", "open");
		if (jobsErr) throw jobsErr;
		openJobsCount = (allOpenJobs || []).filter(
			(j: any) => extractRecruiterId(j.assigned_recruiter) === filters.userId
		).length;
	} else {
		const { count } = await supabase.from("jobs").select("*", { count: "exact", head: true }).eq("status", "open");
		openJobsCount = count || 0;
	}

	const { count: activeCandidates } = filters.userId
		? await supabase.from("candidates").select("id", { count: "exact", head: true }).in("status", ["open", "looking", "new"]).eq("manager", filters.userId)
		: await supabase.from("candidates").select("id", { count: "exact", head: true }).in("status", ["open", "looking", "new"]);

	// For matches and applications, filter by user's jobs
	let matchesInProgress = 0;
	let pendingMatches = 0;

	const closedFilter = `(hired,${CLOSED_MATCH_STATUSES.join(",")})`;
	if (filters.userId) {
		const jobIds = await getJobIdsForRecruiter(filters.userId);
		if (jobIds.length > 0) {
			const { count: mc } = await supabase
				.from("matches")
				.select("id", { count: "exact", head: true })
				.not("status", "in", closedFilter)
				.not("status", "is", null)
				.in("job_id", jobIds);
			matchesInProgress = mc || 0;

			// Pending matches = early stages (matched, applied, sent)
			const { count: pm } = await supabase
				.from("matches")
				.select("id", { count: "exact", head: true })
				.in("status", ["matched", "applied", "sent"])
				.in("job_id", jobIds);
			pendingMatches = pm || 0;
		}
	} else {
		const [{ count: mc }, { count: pm }] = await Promise.all([
			supabase.from("matches").select("id", { count: "exact", head: true }).not("status", "in", closedFilter).not("status", "is", null),
			supabase.from("matches").select("id", { count: "exact", head: true }).in("status", ["matched", "applied", "sent"]),
		]);
		matchesInProgress = mc || 0;
		pendingMatches = pm || 0;
	}

	return {
		openJobs: openJobsCount,
		activeCandidates: activeCandidates || 0,
		matchesInProgress,
		pendingMatches,
	};
}

// KPI #6 + #6b combined: Historical Velocity — matches created, interviews, hires per week (from MIN_DATE onwards)
async function fetchHistoricalVelocity(filters: ResolvedFilters): Promise<HistoricalVelocityData[]> {
	const minDateISO = MIN_DATE.toISOString();

	// Fetch matches (from MIN_DATE, user filter via job IDs)
	let matchQuery = supabase.from("matches").select("id, job_id, created_at").gte("created_at", minDateISO);
	let jobIds: string[] | null = null;
	if (filters.userId) {
		jobIds = await getJobIdsForRecruiter(filters.userId);
		if (jobIds.length > 0)
			matchQuery = matchQuery.in("job_id", jobIds);
		else
			return [];
	}
	const { data: matchData, error: matchError } = await matchQuery;
	if (matchError) throw matchError;

	// Fetch history events (interviews + hires, from MIN_DATE, user filter via changed_by)
	let histQuery = supabase
		.from("history_matches" as any)
		.select("changed_at, new_value, changed_fields, changed_by")
		.gte("changed_at", minDateISO);

	if (filters.userId)
		histQuery = histQuery.eq("changed_by", filters.userId);
	const { data: histData, error: histError } = await histQuery;
	if (histError) throw histError;

	const weekMap = new Map<string, { matches: number; interviews: number; hires: number }>();

	const getOrInit = (week: string) => {
		if (!weekMap.has(week)) weekMap.set(week, { matches: 0, interviews: 0, hires: 0 });
		return weekMap.get(week)!;
	};

	(matchData || []).forEach((m: any) => {
		if (m.created_at) getOrInit(getISOWeek(new Date(m.created_at))).matches++;
	});

	(histData || []).forEach((row: any) => {
		const toStatus = row.new_value?.status;
		if (toStatus === "interview") getOrInit(getISOWeek(new Date(row.changed_at))).interviews++;
		if (toStatus === "hired" && row.new_value?.status !== row.old_value?.status)
			getOrInit(getISOWeek(new Date(row.changed_at))).hires++;
	});

	return Array.from(weekMap.entries())
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([period, vals]) => ({ period, ...vals }));
}

async function fetchCandidatesCreated(filters: ResolvedFilters): Promise<number> {
	let query = supabase.from("candidates").select("id", { count: "exact", head: true });

	if (filters.dateFrom)
		query = query.gte("created_at", filters.dateFrom);
	if (filters.dateTo)
		query = query.lte("created_at", filters.dateTo);
	if (filters.userId)
		query = query.eq("manager", filters.userId);

	const { count, error } = await query;
	if (error) throw error;

	return count || 0;
}

async function fetchCandidateCreationTrend(filters: ResolvedFilters): Promise<CandidateCreationTrendData[]> {
	let query = supabase.from("candidates").select("created_at, manager, status");

	if (filters.dateFrom)
		query = query.gte("created_at", filters.dateFrom);
	if (filters.dateTo)
		query = query.lte("created_at", filters.dateTo);
	if (filters.userId)
		query = query.eq("manager", filters.userId);

	const { data, error } = await query;
	if (error) throw error;

	// Group by period AND status
	const periodStatusMap = new Map<string, Map<string, number>>();
	const allStatuses = new Set<string>();

	(data || []).forEach((c: any) => {
		const date = new Date(c.created_at);
		const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
		const status = c.status || "Unknown";

		allStatuses.add(status);

		if (!periodStatusMap.has(period))
			periodStatusMap.set(period, new Map());

		const statusCounts = periodStatusMap.get(period)!;
		statusCounts.set(status, (statusCounts.get(status) || 0) + 1);
	});

	// Convert to chart data format with status as dynamic keys
	return Array.from(periodStatusMap.entries())
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([period, statusCounts]) => {
			const total = Array.from(statusCounts.values()).reduce((sum, count) => sum + count, 0);
			const row: CandidateCreationTrendData = { period, count: total };

			// Add each status as a dynamic property
			statusCounts.forEach((count, status) => {
				row[status] = count;
			});

			return row;
		});
}

async function fetchCandidatesByStatus(filters: ResolvedFilters): Promise<CandidatesByStatusData[]> {
	let query = supabase.from("candidates").select("status, manager, created_at");

	if (filters.dateFrom)
		query = query.gte("created_at", filters.dateFrom);
	if (filters.dateTo)
		query = query.lte("created_at", filters.dateTo);
	if (filters.userId)
		query = query.eq("manager", filters.userId);

	const { data, error } = await query;
	if (error) throw error;

	const statusMap = new Map<string, number>();
	(data || []).forEach((c: any) => {
		const status = c.status || "Unknown";
		statusMap.set(status, (statusMap.get(status) || 0) + 1);
	});

	return Array.from(statusMap.entries())
		.map(([status, count]) => ({ status, count }))
		.sort((a, b) => b.count - a.count);
}

async function fetchWorkforceKPIs(filters: ResolvedFilters): Promise<WorkforceKPIData> {
	const WORKING_DAYS_PER_MONTH = 22;

	let query = supabase.from("workforce").select(`
		id, status, is_bench, start_date, end_date, expected_end_date,
		candidate_id, created_at, assigned_recruiter,
		purchase_price_daily, purchase_price_hourly,
		selling_price_daily, selling_price_hourly,
		bench_daily_cost, bench_reason,
		contract_type, billing_type, work_percentage,
		candidates(name),
		jobs(title)
	`);

	if (filters.userId)
		query = query.eq("assigned_recruiter", filters.userId);

	const { data, error } = await query;
	if (error) throw error;

	const rows = data || [];

	// Status counts
	const statusCounts = new Map<string, number>();
	let activeAssignments = 0;
	let benchCount = 0;
	let onHoldCount = 0;
	let completedCount = 0;
	let terminatedCount = 0;

	rows.forEach((w: any) => {
		const status = w.status || "unknown";
		statusCounts.set(status, (statusCounts.get(status) || 0) + 1);

		if (status === "active" && !w.is_bench) activeAssignments++;
		if (w.is_bench && (status === "active" || status === "on_bench")) benchCount++;
		if (status === "on_hold") onHoldCount++;
		if (status === "completed") completedCount++;
		if (status === "terminated") terminatedCount++;
	});

	// Average assignment duration (for completed/terminated assignments with start+end dates)
	const completedWithDates = rows.filter((w: any) =>
		(w.status === "completed" || w.status === "terminated") && w.start_date && w.end_date
	);
	let avgAssignmentDuration: number | null = null;
	if (completedWithDates.length > 0) {
		const totalDays = completedWithDates.reduce((sum: number, w: any) => {
			const days = (new Date(w.end_date).getTime() - new Date(w.start_date).getTime()) / 86400000;
			return sum + Math.max(0, days);
		}, 0);
		avgAssignmentDuration = Math.round((totalDays / completedWithDates.length) * 10) / 10;
	}

	// Utilization rate: active (non-bench) / total active (including bench)
	const totalActiveIncBench = rows.filter((w: any) => w.status === "active" || w.status === "on_bench").length;
	const utilizationRate = totalActiveIncBench > 0
		? Math.round((activeAssignments / totalActiveIncBench) * 100)
		: 0;

	// By status
	const byStatus = Array.from(statusCounts.entries())
		.map(([status, count]) => ({ status, count }))
		.sort((a, b) => b.count - a.count);

	// Assignment trend (created vs ended by month, within date filters)
	const periodMap = new Map<string, { created: number; ended: number }>();
	rows.forEach((w: any) => {
		if (w.created_at) {
			const d = new Date(w.created_at);
			if ((!filters.dateFrom || w.created_at >= filters.dateFrom) && (!filters.dateTo || w.created_at <= filters.dateTo)) {
				const period = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
				const entry = periodMap.get(period) || { created: 0, ended: 0 };
				entry.created++;
				periodMap.set(period, entry);
			}
		}
		if (w.end_date && (w.status === "completed" || w.status === "terminated")) {
			const d = new Date(w.end_date);
			if ((!filters.dateFrom || w.end_date >= filters.dateFrom) && (!filters.dateTo || w.end_date <= filters.dateTo)) {
				const period = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
				const entry = periodMap.get(period) || { created: 0, ended: 0 };
				entry.ended++;
				periodMap.set(period, entry);
			}
		}
	});

	const assignmentTrend = Array.from(periodMap.entries())
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([period, vals]) => ({ period, ...vals }));

	// --- Financial KPIs ---
	const activeRows = rows.filter((w: any) => w.status === "active");

	let dailyRevenue = 0;
	let dailyCost = 0;
	let dailyBenchCost = 0;

	activeRows.forEach((w: any) => {
		if (!w.is_bench) {
			const sell = parseFloat(w.selling_price_daily) || 0;
			const buy = parseFloat(w.purchase_price_daily) || 0;
			dailyRevenue += sell;
			dailyCost += buy;
		} else {
			dailyBenchCost += parseFloat(w.bench_daily_cost) || 0;
		}
	});

	// Also count bench cost from on_bench and on_hold statuses
	rows.filter((w: any) => w.status === "on_bench" || w.status === "on_hold").forEach((w: any) => {
		dailyBenchCost += parseFloat(w.bench_daily_cost) || 0;
	});

	const dailyMargin = dailyRevenue - dailyCost;
	const marginPct = dailyRevenue > 0 ? Math.round((dailyMargin / dailyRevenue) * 100) : 0;

	const financials: WorkforceFinancials = {
		dailyRevenue: Math.round(dailyRevenue * 100) / 100,
		dailyCost: Math.round(dailyCost * 100) / 100,
		dailyMargin: Math.round(dailyMargin * 100) / 100,
		marginPct,
		dailyBenchCost: Math.round(dailyBenchCost * 100) / 100,
		monthlyRevenue: Math.round(dailyRevenue * WORKING_DAYS_PER_MONTH * 100) / 100,
		monthlyCost: Math.round(dailyCost * WORKING_DAYS_PER_MONTH * 100) / 100,
		monthlyMargin: Math.round(dailyMargin * WORKING_DAYS_PER_MONTH * 100) / 100,
		monthlyBenchCost: Math.round(dailyBenchCost * WORKING_DAYS_PER_MONTH * 100) / 100,
	};

	// --- Contract type breakdown ---
	const contractMap = new Map<string, number>();
	rows.forEach((w: any) => {
		const ct = w.contract_type || "unknown";
		contractMap.set(ct, (contractMap.get(ct) || 0) + 1);
	});
	const contractTypes: ContractTypeBreakdown[] = Array.from(contractMap.entries())
		.map(([type, count]) => ({ type, count }))
		.sort((a, b) => b.count - a.count);

	// --- Bench reason breakdown ---
	const benchReasonMap = new Map<string, { count: number; totalCost: number }>();
	rows.filter((w: any) => w.is_bench || w.status === "on_bench").forEach((w: any) => {
		const reason = w.bench_reason || "unknown";
		const entry = benchReasonMap.get(reason) || { count: 0, totalCost: 0 };
		entry.count++;
		entry.totalCost += parseFloat(w.bench_daily_cost) || 0;
		benchReasonMap.set(reason, entry);
	});
	const benchReasons: BenchReasonBreakdown[] = Array.from(benchReasonMap.entries())
		.map(([reason, v]) => ({ reason, count: v.count, dailyCost: Math.round(v.totalCost * 100) / 100 }))
		.sort((a, b) => b.count - a.count);

	// --- Expiring soon (within 30 days) ---
	const now = new Date();
	const in30Days = new Date(now.getTime() + 30 * 86400000);
	const expiringSoon: ExpiringAssignment[] = rows
		.filter((w: any) => {
			if (w.status !== "active" || !w.expected_end_date) return false;
			const end = new Date(w.expected_end_date);
			return end >= now && end <= in30Days;
		})
		.map((w: any) => ({
			id: w.id,
			candidateName: (w as any).candidates?.name || "—",
			jobTitle: (w as any).jobs?.title || "—",
			expectedEndDate: w.expected_end_date,
			daysRemaining: Math.ceil((new Date(w.expected_end_date).getTime() - now.getTime()) / 86400000),
			dailyRevenue: w.selling_price_daily ? parseFloat(w.selling_price_daily) : null,
		}))
		.sort((a: ExpiringAssignment, b: ExpiringAssignment) => a.daysRemaining - b.daysRemaining);

	return {
		totalAssignments: rows.length,
		activeAssignments,
		benchCount,
		onHoldCount,
		completedCount,
		terminatedCount,
		avgAssignmentDuration,
		utilizationRate,
		byStatus,
		assignmentTrend,
		financials,
		contractTypes,
		benchReasons,
		expiringSoon,
	};
}

export function useDashboardKPIs(filters?: KPIFilterState) {
	const { user } = useAuth();

	// Resolve "me" to actual user ID
	const resolvedUserId = filters?.userId === "me" ? (user?.id || null) : (filters?.userId || null);

	// Enforce minimum date (Feb 1, 2026) for match history
	const dateFrom = enforceMinDate(filters?.dateFrom || null);
	const dateTo = filters?.dateTo || null;

	const resolvedFilters: ResolvedFilters = {
		userId: resolvedUserId,
		dateFrom: dateFrom ? dateFrom.toISOString() : null,
		dateTo: dateTo ? dateTo.toISOString() : null,
	};

	return useQuery<DashboardKPIs>({
		queryKey: ["dashboardKPIs", resolvedFilters.userId, resolvedFilters.dateFrom, resolvedFilters.dateTo],
		queryFn: async () => {
			const [
				pipelineFunnel,
				matchesPerJob,
				{ conversions: conversionRates, dropOffs: dropOffRates },
				timeToHire,
				timeToFill,
				timeInStage,
				publicAppRatio,
				aiScoreVsOutcome,
				scoreDistribution,
				recruiters,
				recruiterActivity,
				offerAcceptanceRate,
				pipelineHealth,
				historicalVelocity,
				candidatesCreated,
				candidateCreationTrend,
				candidatesByStatus,
				workforceKPIs,
			] = await Promise.all([
				fetchPipelineFunnel(resolvedFilters),
				fetchMatchesPerJob(resolvedFilters),
				fetchConversionRates(resolvedFilters),
				fetchTimeToHire(resolvedFilters),
				fetchTimeToFill(resolvedFilters),
				fetchTimeInStage(resolvedFilters),
				fetchPublicAppRatio(resolvedFilters),
				fetchAiScoreVsOutcome(resolvedFilters),
				fetchScoreDistribution(resolvedFilters),
				fetchRecruiters(resolvedFilters),
				fetchRecruiterActivity(resolvedFilters),
				fetchOfferAcceptanceRate(resolvedFilters),
				fetchPipelineHealth(resolvedFilters),
				fetchHistoricalVelocity(resolvedFilters),
				fetchCandidatesCreated(resolvedFilters),
				fetchCandidateCreationTrend(resolvedFilters),
				fetchCandidatesByStatus(resolvedFilters),
				fetchWorkforceKPIs(resolvedFilters),
			]);

			return {
				pipelineFunnel,
				matchesPerJob,
				conversionRates,
				dropOffRates,
				timeToHire,
				timeToFill,
				timeInStage,
				publicAppRatio,
				aiScoreVsOutcome,
				scoreDistribution,
				recruiters,
				recruiterActivity,
				offerAcceptanceRate,
				pipelineHealth,
				historicalVelocity,
				candidatesCreated,
				candidateCreationTrend,
				candidatesByStatus,
				workforceKPIs,
				resolvedDateFrom: resolvedFilters.dateFrom,
				resolvedDateTo: resolvedFilters.dateTo,
			};
		},
		staleTime: 5 * 60 * 1000,
	});
}
